import React, { useEffect, useState } from "react";
import { Plus, Copy, Check, Trash2, KeyRound, AlertTriangle, Code2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export default function AdminApiKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // {name, key, key_prefix}
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/api-keys");
      setKeys(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load API keys");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please name this key");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name: name.trim() });
      setNewKey(data);
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Requests using this key will be rejected.`)) return;
    try {
      await api.delete(`/admin/api-keys/${k.id}`);
      toast.success("Key revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const apiBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
  const sampleCurl = `curl -X POST '${apiBase}/api/public/v1/leads' \\
  -H 'Content-Type: application/json' \\
  -H 'X-API-Key: YOUR_KEY_HERE' \\
  -d '{
    "company_name": "Acme Inc",
    "client_poc_name": "Jane Doe",
    "client_poc_email": "jane@acme.com",
    "client_poc_number": "+91-9999999999",
    "utm_source": "google",
    "utm_medium": "cpc",
    "utm_campaign": "spring_launch",
    "utm_content": "hero_cta",
    "utm_term": "lead management"
  }'`;

  return (
    <Layout title="API Keys" breadcrumb={["Admin", "API Keys"]}>
      <div className="space-y-6">
        {/* Issue new key panel */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="api-keys-create-panel">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={16} className="text-orange-500" />
            <h2 className="text-sm font-semibold text-slate-900">Issue a new API key</h2>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Use these keys to authenticate lead-capture requests from your marketing website, landing pages, or partner systems.
            The full key is shown <b>only once</b> — copy it immediately.
          </p>
          <form onSubmit={create} className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="api-key-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Label (e.g. Marketing Website, LP — Spring Sale)"
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
            <button
              data-testid="api-key-create-btn"
              type="submit"
              disabled={creating}
              className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-medium inline-flex items-center gap-1.5"
            >
              <Plus size={14} /> {creating ? "Generating…" : "Generate key"}
            </button>
          </form>
        </div>

        {/* Just-created key banner */}
        {newKey && (
          <div
            data-testid="api-key-just-created"
            className="bg-amber-50 border border-amber-300 rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-amber-900">
                  Copy this key now — you won't see it again
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  For "{newKey.name}". Store it in your CMS or environment variables.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-md px-3 py-2 font-mono text-xs text-slate-800 break-all">
              <span className="flex-1" data-testid="api-key-raw-value">{newKey.key}</span>
              <button
                data-testid="api-key-copy-btn"
                onClick={copyKey}
                className="shrink-0 px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium inline-flex items-center gap-1"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button
              data-testid="api-key-dismiss-btn"
              onClick={() => setNewKey(null)}
              className="mt-3 text-xs text-amber-800 hover:text-amber-900 underline"
            >
              I've saved it — dismiss
            </button>
          </div>
        )}

        {/* Existing keys list */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="api-keys-list-panel">
          <div className="px-5 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">Existing keys</h2>
          </div>
          {keys.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="api-keys-empty">
              No keys yet. Generate one above to start accepting external leads.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-2.5">Name</th>
                    <th className="px-5 py-2.5">Prefix</th>
                    <th className="px-5 py-2.5">Created</th>
                    <th className="px-5 py-2.5">Last used</th>
                    <th className="px-5 py-2.5">Uses</th>
                    <th className="px-5 py-2.5">Status</th>
                    <th className="px-5 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id} data-testid={`api-key-row-${k.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-5 py-3 font-medium text-slate-900">{k.name}</td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-500">{k.key_prefix}…</td>
                      <td className="px-5 py-3 text-slate-600 text-xs">{fmtDate(k.created_at)}</td>
                      <td className="px-5 py-3 text-slate-600 text-xs">{fmtDate(k.last_used_at)}</td>
                      <td className="px-5 py-3 text-slate-700">{k.usage_count || 0}</td>
                      <td className="px-5 py-3">
                        {k.active ? (
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Active
                          </span>
                        ) : (
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                            Revoked
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {k.active && (
                          <button
                            data-testid={`api-key-revoke-${k.id}`}
                            onClick={() => revoke(k)}
                            className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50"
                          >
                            <Trash2 size={12} /> Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Integration snippet */}
        <div className="bg-slate-900 text-slate-100 rounded-xl shadow-sm overflow-hidden" data-testid="api-keys-docs">
          <div className="px-5 py-3 border-b border-slate-700 flex items-center gap-2">
            <Code2 size={15} className="text-orange-400" />
            <h2 className="text-sm font-semibold">Integration example</h2>
            <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400 font-medium">
              POST /api/public/v1/leads
            </span>
          </div>
          <pre className="p-5 text-xs overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">{sampleCurl}</pre>
          <div className="px-5 py-3 border-t border-slate-700 text-[11px] text-slate-400">
            <b className="text-slate-200">Tip:</b> UTM parameters can also be passed as query-string values (e.g. <code>?utm_source=google&amp;utm_campaign=spring</code>) — query-string values override body values. Only <code>company_name</code> is required.
          </div>
        </div>
      </div>
    </Layout>
  );
}
